/**
 * useWeekSessions — aggregates sessions across all the tutor's active contracts
 * for a given week, using parallel API calls.
 *
 * Returns:
 *   sessions    {Array}   flat list of sessions from all contracts (with contract_id)
 *   contracts   {Array}   list of contracts
 *   loading     {boolean}
 *   error       {string|null}
 *   refresh     {()=>void}  re-fetches all data
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import apiService from '../services/apiService';
import { toDateString, getWeekEnd } from '../utils/weekMapping';

/**
 * @param {Date} weekStart - Monday of the week to fetch sessions for
 */
export function useWeekSessions(weekStart) {
  const [sessions, setSessions] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Track latest weekStart to avoid stale updates
  const weekStartRef = useRef(weekStart);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);

    const currentWeekStart = weekStartRef.current;
    const dateFrom = toDateString(currentWeekStart);
    const weekEnd = getWeekEnd(currentWeekStart);
    const dateTo = toDateString(weekEnd);

    try {
      // 1. Fetch all contracts for this tutor
      const contractsResponse = await apiService.getContracts();
      const contractList = contractsResponse?.data ?? contractsResponse ?? [];

      if (weekStartRef.current !== currentWeekStart) return; // stale

      setContracts(Array.isArray(contractList) ? contractList : []);

      if (!contractList.length) {
        setSessions([]);
        setLoading(false);
        return;
      }

      // 2. Fetch sessions for each contract in parallel
      const results = await Promise.all(
        contractList.map((contract) =>
          apiService
            .getSessions(contract.id, { date_from: dateFrom, date_to: dateTo })
            .then((res) => {
              const data = res?.data ?? res ?? [];
              return (Array.isArray(data) ? data : []).map((s) => ({
                ...s,
                contract_id: s.contract_id ?? contract.id,
              }));
            })
            .catch(() => []) // individual contract failure → empty (don't break others)
        )
      );

      if (weekStartRef.current !== currentWeekStart) return; // stale

      const merged = results.flat();
      setSessions(merged);
    } catch (err) {
      if (weekStartRef.current !== currentWeekStart) return;
      setError(err?.userMessage || 'Erreur lors du chargement du calendrier.');
    } finally {
      if (weekStartRef.current === currentWeekStart) {
        setLoading(false);
      }
    }
  }, []); // intentionally no deps: weekStartRef is mutable

  // When weekStart changes, update ref and re-fetch
  useEffect(() => {
    weekStartRef.current = weekStart;
    fetch();
  }, [weekStart, fetch]);

  return { sessions, contracts, loading, error, refresh: fetch };
}
